import { ActiveInactiveFilterState } from '../components/common/activeInactiveToggle';
import { buildFilterQuery } from '../components/common/filters/helper';
import { TableSort } from '../utils/dataTable/data';
import { SortStatus } from '../components/common/sortButton';
import { Features, isFeatureEnabled } from '../featureToggles/features';

// TODO: remove this AnyObject
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyObject = { [key: string | number | symbol]: any };

export interface Pagination {
  offset: number;
  limit: number;
  // use for Features.PlgExternal3
  nextPage?: string;
  // end of use for Features.PlgExternal3
}

export interface ScrollPosition {
  x: number;
  y: number;
}

export interface ListParams<T> {
  filters?: T;
  search?: T;
  active?: ActiveInactiveFilterState;
  status?: ActiveInactiveFilterState;
  pagination?: Pagination;
  sort?: TableSort;
  accountId?: string;
}

export function getFilterAsMap<T extends AnyObject>(params: ListParams<T>): Map<string, string | number> {
  // TODO replace search support for teams in backend and delete this old functions, use buildQueryParams instead
  const queryParams: [string, string | number][] = [];
  if (params.filters) {
    const filter = params.filters || {} as T;
    const accountFilterAsQuery = Object.keys(filter).map(key => ({
      [key]: filter[key as keyof T]
    }));
    if (accountFilterAsQuery.length) {
      queryParams.push(['filters', JSON.stringify(accountFilterAsQuery)]);
    }
  }
  if (params.pagination) {
    queryParams.push(['offset', params.pagination.offset]);
    queryParams.push(['limit', params.pagination.limit]);
  }
  if (params.sort) {
    if (params.sort.status !== SortStatus.None) {
      queryParams.push(['sortBy', params.sort.key as string || '']);
      queryParams.push(['sort', params.sort.status]);
    }
  }
  return new Map<string, string | number>(queryParams);
}

export const buildQueryParams = <T>(params: ListParams<T>): string => {
  const result: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    switch (key) {
    case 'filters':
    case 'search':
      result.push(buildFilterQuery(value, key));
      break;
    case 'status':
      if (value !== ActiveInactiveFilterState.All) {
        result.push(`${key}=${value.toLowerCase()}`);
      }
      break;
    case 'active':
      if (value !== ActiveInactiveFilterState.All) {
        result.push(`${key}=${value === ActiveInactiveFilterState.Active}`);
      }
      break;
    case 'pagination':
      result.push(`offset=${value.offset}`);
      result.push(`limit=${value.limit}`);
      if (isFeatureEnabled(Features.PlgExternal3) && value.nextPage) {
        result.push(`nextPage=${value.nextPage}`);
      }
      break;
    case 'sort':
      if (value.status !== SortStatus.None) {
        result.push(`sortBy=${value.key}`);
        result.push(`sort=${value.status}`);
      }
      break;
    default:
      if (value !== undefined) {
        result.push(`${key}=${value}`);
      }
      break;
    }
  });
  const query = result.filter(query => query.length).join('&');
  return (query.length) ? `?${query}` : '';
};

export enum ActionsBulk {
  Deactivate = 'deactivate',
  Activate = 'activate'
}

export interface ListBulkParams<T> {
  action?: ActionsBulk;
  filters?: T;
  search?: T;
  active?: boolean;
  except?: React.Key[];
  ids?: React.Key[];
}

type IErrorResponse = {
  message?: string;
  errors?: string;
};

export const isResponseError = (error: unknown): error is IErrorResponse => {
  return typeof error === 'object' &&
      error !== null &&
      (('message' in error &&
      (typeof error.message === 'string' || typeof error.message === 'undefined')) ||
      ('errors' in error &&
      (typeof error.errors === 'string' || typeof error.errors === 'undefined')));
};

const EMPTY_ARRAY: readonly never[] = Object.freeze([]);

export function getEmptyArray<T>(): readonly T[] {
  return EMPTY_ARRAY as readonly T[];
}